import { Link } from "react-router-dom";
import { BookOpen, Home as HomeIcon, UtensilsCrossed, HeartHandshake, Leaf, LifeBuoy, Compass, ArrowRight } from "lucide-react";
import {
  HubPage, Container, SectionHead, CommunityCTA, BetaCTA, Band, HeroMedia, Seo,
} from "../components/hub/HubKit";
import { FIND_GROUPS, findsByGroup, findLabel, FINDS_GENERAL_DISCLOSURE, PAGE_IMAGES, CATEGORIES } from "../data/hub";
import { track } from "../lib/analytics";

const GROUP_META = {
  books: { icon: BookOpen, accent: "#b17a6b" },
  comfort: { icon: HomeIcon, accent: "#5c8a7e" },
  "food-kitchen": { icon: UtensilsCrossed, accent: "#c08a45" },
  wellness: { icon: HeartHandshake, accent: "#6f8f5c" },
  earth: { icon: Leaf, accent: "#6a9350" },
  tools: { icon: LifeBuoy, accent: "#77808f" },
};

const FindCard = ({ find, accent }) => {
  const label = findLabel(find);
  return (
    <div
      onClick={() => track("finds_clicked", find.title)}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-[1.4rem] border border-line bg-white/75 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(47,75,57,0.1)]"
      data-testid="find-card"
    >
      {find.image && (
        <div className="relative aspect-[16/10] overflow-hidden bg-cream-deep/40">
          <img
            src={find.image}
            alt={find.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            data-testid="find-card-image"
          />
          {label && (
            <span
              className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[0.55rem] font-semibold uppercase tracking-[0.14em] backdrop-blur"
              style={{ color: label.tone }}
              data-testid="find-label"
            >
              {label.text}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-1 flex-col p-6">
        <span className="text-[0.6rem] font-semibold uppercase tracking-[0.16em]" style={{ color: accent }}>{find.tag}</span>
        <h3 className="mt-2 font-serif text-lg font-semibold text-heading">{find.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-ink/70">{find.note}</p>
        {find.why && (
          <p className="mt-3 flex-1 text-xs leading-relaxed text-ink/55">
            <span className="font-semibold" style={{ color: accent }}>Why HEARD picked it:</span> {find.why}
          </p>
        )}
        {find.link && (
          <a
            href={find.link}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium hover:opacity-70"
            style={{ color: accent }}
            data-testid="find-card-link"
          >
            {find.partner ? `View at ${find.partner}` : "Take a look"} <ArrowRight size={14} />
          </a>
        )}
        {find.affiliateDisclosure && <p className="mt-2 text-[0.7rem] italic text-ink/50">{find.affiliateDisclosure}</p>}
      </div>
    </div>
  );
};

export default function Finds() {
  return (
    <HubPage testid="finds-page">
      <Seo
        title="HEARD Finds — Curated Things Worth a Look"
        description="A gently curated, editorial collection of books, home & comfort ideas, food & kitchen finds, wellness picks, earth-friendly swaps, and helpful tools that may make everyday life easier."
      />

      <Band tone="deep" className="border-b border-line/60">
        <Container className="pt-14 pb-12 sm:pt-20">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_0.8fr]">
            <SectionHead
              overline="HEARD Finds"
              title="Things we think are worth a look."
              sub="A gently curated mix of tools, reads, and ideas — chosen by our team, not by advertisers. This is discovery, not a store."
            />
            <HeroMedia src={PAGE_IMAGES.finds} alt="An elevated flat-lay of soft ceramic and linen in neutral tones" eager className="aspect-[16/10] lg:aspect-[5/4]" />
          </div>
        </Container>
      </Band>

      {FIND_GROUPS.map((group, gi) => {
        const items = findsByGroup(group.key);
        if (items.length === 0) return null;
        const meta = GROUP_META[group.key] || { icon: Compass, accent: "#3E5A44" };
        const GIcon = meta.icon;
        const tone = gi % 2 === 0 ? "cream" : "white";
        return (
          <Band key={group.key} tone={tone} className={`py-12 sm:py-16 ${tone === "white" ? "border-y border-line/50" : ""}`}>
            <Container>
              <section data-testid={`finds-group-${group.key}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ backgroundColor: `${meta.accent}1f`, color: meta.accent }}>
                    <GIcon size={20} strokeWidth={1.8} />
                  </span>
                  <h2 className="serif-display text-2xl text-charcoal">{group.label}</h2>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((f) => <FindCard key={f.title} find={f} accent={meta.accent} />)}
                </div>
              </section>
            </Container>
          </Band>
        );
      })}

      <Container className="py-14 sm:py-16">
        <div className="rounded-2xl border border-line bg-cream-deep/50 px-6 py-6" data-testid="finds-disclosure">
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-sage-deep">How HEARD Finds works</h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/70">{FINDS_GENERAL_DISCLOSURE}</p>
        </div>
      </Container>

      {/* Keep exploring — subtle internal discovery into resources/categories */}
      <Band tone="white" className="border-y border-line/60 py-12 sm:py-14">
        <Container>
          <section data-testid="finds-keep-exploring">
            <h2 className="serif-display text-2xl text-charcoal">Keep exploring HEARD</h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-ink/60">Gentle, practical resources across the parts of everyday life.</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/resources/${c.slug}`}
                  onClick={() => track("category_selected", c.slug)}
                  data-testid={`finds-explore-${c.slug}`}
                  className="rounded-full border border-line bg-white/70 px-4 py-2 text-sm text-sage-deep transition-colors hover:border-sage hover:bg-white"
                >
                  {c.title}
                </Link>
              ))}
            </div>
          </section>
        </Container>
      </Band>

      <Band tone="deep" className="py-14 sm:py-20">
        <Container>
          <BetaCTA />
          <div className="mt-8"><CommunityCTA /></div>
        </Container>
      </Band>
    </HubPage>
  );
}
